import { diffJson } from './session-diff.js';
import { buildPerMethodStats } from './method-stats.js';
import { extractSessionMetadata } from './session-metadata.js';
/**
 * The stable, high-signal slice of metadata. `extensions` (JetBrains
 * `proxyConfig` carries per-session-rotating `proxy_key`s) is excluded on
 * purpose — it's editor-specific and volatile, exactly the noise this view
 * exists to avoid.
 */
function comparableMetadata(m) {
    return {
        protocolVersion: m.protocolVersion,
        client: m.client,
        agent: m.agent,
        clientCapabilities: m.clientCapabilities,
        agentCapabilities: m.agentCapabilities,
        runtime: m.runtime,
    };
}
export function buildMetadataDiff(a, b) {
    const am = extractSessionMetadata(a);
    const bm = extractSessionMetadata(b);
    return {
        a: am,
        b: bm,
        changes: diffJson(comparableMetadata(am), comparableMetadata(bm)),
    };
}
/**
 * Join per-method stats of two sessions by (method, kind) and compute deltas.
 * Sorted by the largest absolute p99 change first (the biggest latency
 * movement), then by count change — so a regression surfaces at the top.
 */
export function buildMethodStatsDiff(a, b) {
    const aStats = buildPerMethodStats(a);
    const bStats = buildPerMethodStats(b);
    const key = (m) => `${m.kind}:${m.method}`;
    const aMap = new Map(aStats.map((m) => [key(m), m]));
    const bMap = new Map(bStats.map((m) => [key(m), m]));
    const out = [];
    for (const k of new Set([...aMap.keys(), ...bMap.keys()])) {
        const am = aMap.get(k) ?? null;
        const bm = bMap.get(k) ?? null;
        const ref = (am ?? bm);
        out.push({
            method: ref.method,
            kind: ref.kind,
            a: am,
            b: bm,
            countDelta: (bm?.count ?? 0) - (am?.count ?? 0),
            p50Delta: deltaOf(am?.p50, bm?.p50),
            p99Delta: deltaOf(am?.p99, bm?.p99),
            maxDelta: deltaOf(am?.max, bm?.max),
        });
    }
    out.sort((x, y) => Math.abs(y.p99Delta ?? 0) - Math.abs(x.p99Delta ?? 0) ||
        Math.abs(y.countDelta) - Math.abs(x.countDelta) ||
        x.method.localeCompare(y.method));
    return out;
}
function deltaOf(a, b) {
    if (a === null || a === undefined || b === null || b === undefined)
        return null;
    return b - a;
}
//# sourceMappingURL=session-compare.js.map